/**
 * The figures in "Matching this month", from avrio-backend's matching usage
 * and limits. Pure, so the arithmetic and the unknown-vs-zero rules are tested
 * directly.
 */
import { MatchingUsage } from '../../types';

export interface Meter {
  used: number;
  limit: number;
  /** 0-100 for the bar; a limit of 0 reads as full, since nothing more can start. */
  percent: number;
  status: 'normal' | 'near' | 'reached';
}

export const meter = (used: number, limit: number): Meter => {
  const percent =
    limit > 0 ? Math.min(100, Math.floor((used * 100) / limit)) : 100;
  return {
    used,
    limit,
    percent,
    status: percent >= 100 ? 'reached' : percent >= 80 ? 'near' : 'normal',
  };
};

export interface UsageSummary {
  jobs: Meter;
  /** Null when the unlock counter could not be read - never shown as zero. */
  unlocks: Meter | null;
  candidatesScored: number | null;
  /** Nothing has happened yet this month: no jobs matched, no scores, no unlocks. */
  quiet: boolean;
}

export const summariseUsage = (usage: MatchingUsage): UsageSummary => {
  const unlocks =
    usage.usageAvailable && typeof usage.unlocks === 'number'
      ? meter(usage.unlocks, usage.monthlyUnlockAllowance)
      : null;
  const candidatesScored =
    usage.usageAvailable && typeof usage.candidatesScored === 'number'
      ? usage.candidatesScored
      : null;
  return {
    jobs: meter(usage.jobsMatched, usage.monthlyJobCap),
    unlocks,
    candidatesScored,
    quiet:
      usage.jobsMatched === 0 &&
      usage.pausedJobs === 0 &&
      (candidatesScored ?? 0) === 0 &&
      (unlocks?.used ?? 0) === 0,
  };
};

/** "October 2026" for a "2026-10" UTC month. */
export const monthLabel = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
};
